import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useDeviceId, DeviceFingerprint } from './useDeviceId';
import { useAuth } from './useAuth';

export interface RegisteredDevice {
  id: string;
  device_id: string;
  device_name: string | null;
  device_type: 'electron' | 'web' | 'mobile';
  location_id: string;
  organization_id: string;
  register_id: string | null;
  is_active: boolean;
  last_seen_at: string | null;
  last_user_id: string | null;
  registered_by: string | null;
  registered_at: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Joined data
  location?: {
    id: string;
    name: string;
  };
}

export interface DeviceApprovalRequest {
  id: string;
  device_id: string;
  device_fingerprint: DeviceFingerprint | null;
  organization_id: string;
  requested_by: string;
  requested_location_id: string;
  requested_device_name: string | null;
  status: 'pending' | 'approved' | 'denied' | 'expired';
  reviewed_by: string | null;
  review_notes: string | null;
  reviewed_at: string | null;
  expires_at: string;
  created_at: string;
  // Joined data
  requester?: {
    first_name: string | null;
    last_name: string | null;
  };
  location?: {
    name: string;
  };
}

export interface DeviceRegistrationStatus {
  isRegistered: boolean;
  isActive: boolean;
  device: RegisteredDevice | null;
  requiresApproval: boolean;
  hasPendingRequest: boolean;
  pendingRequest: DeviceApprovalRequest | null;
  wasDenied: boolean;
  deniedRequest: DeviceApprovalRequest | null;
}

/**
 * Hook to manage device registration and approval
 */
export function useDeviceRegistration() {
  const queryClient = useQueryClient();
  const { deviceId, deviceType, fingerprint, isLoading: deviceLoading, isElectron } = useDeviceId();
  const { profile } = useAuth();

  const isCashier = profile?.role === 'cashier' || profile?.role === 'employee';
  const isAdmin = profile?.role && ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(profile.role);

  // Check if the current device is registered
  const {
    data: registrationStatus,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['device-registration', deviceId, profile?.organization_id],
    queryFn: async (): Promise<DeviceRegistrationStatus> => {
      // This should not run if not enabled, but double-check
      if (!deviceId || !profile?.organization_id) {
        throw new Error('Missing required data');
      }

      // Check for registered device - try with join first, fall back to simple query
      let device: RegisteredDevice | null = null;
      
      try {
        // First try with location join
        const { data: deviceWithLocation, error: deviceError } = await supabase
          .from('registered_devices')
          .select(`
            *,
            location:locations(id, name)
          `)
          .eq('device_id', deviceId)
          .eq('organization_id', profile.organization_id)
          .maybeSingle();

        if (!deviceError) {
          device = deviceWithLocation as RegisteredDevice | null;
        } else if (deviceError.code !== 'PGRST116') {
          // If it's a 406 or other error, try simple query without join
          console.warn('[DeviceRegistration] Join query failed, trying simple query:', deviceError.code);
          const { data: simpleDevice, error: simpleError } = await supabase
            .from('registered_devices')
            .select('*')
            .eq('device_id', deviceId)
            .eq('organization_id', profile.organization_id)
            .maybeSingle();
          
          if (!simpleError) {
            device = simpleDevice as RegisteredDevice | null;
          }
        }
      } catch (err) {
        console.error('[DeviceRegistration] Error checking device registration:', err);
      }

      // Check for pending approval request - try with join, fall back gracefully
      let pendingRequest: DeviceApprovalRequest | null = null;
      
      try {
        const { data: pendingWithJoin, error: pendingError } = await supabase
          .from('device_approval_requests')
          .select(`
            *,
            requester:profiles!requested_by(first_name, last_name),
            location:locations!requested_location_id(name)
          `)
          .eq('device_id', deviceId)
          .eq('organization_id', profile.organization_id)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .maybeSingle();

        if (!pendingError) {
          pendingRequest = pendingWithJoin as DeviceApprovalRequest | null;
        } else if (pendingError.code !== 'PGRST116') {
          // Fall back to simple query
          console.warn('[DeviceRegistration] Pending request join query failed:', pendingError.code);
          const { data: simplePending } = await supabase
            .from('device_approval_requests')
            .select('*')
            .eq('device_id', deviceId)
            .eq('organization_id', profile.organization_id)
            .eq('status', 'pending')
            .gt('expires_at', new Date().toISOString())
            .maybeSingle();
          
          pendingRequest = simplePending as DeviceApprovalRequest | null;
        }
      } catch (err) {
        console.warn('[DeviceRegistration] Error checking pending requests:', err);
      }

      // Check for recently denied request (within last 5 minutes)
      let deniedRequest: DeviceApprovalRequest | null = null;
      
      try {
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const { data: deniedWithJoin, error: deniedError } = await supabase
          .from('device_approval_requests')
          .select(`
            *,
            requester:profiles!requested_by(first_name, last_name),
            location:locations!requested_location_id(name)
          `)
          .eq('device_id', deviceId)
          .eq('organization_id', profile.organization_id)
          .eq('status', 'denied')
          .gt('reviewed_at', fiveMinutesAgo)
          .order('reviewed_at', { ascending: false })
          .limit(1);

        if (!deniedError && deniedWithJoin?.length) {
          deniedRequest = deniedWithJoin[0] as DeviceApprovalRequest;
        } else if (deniedError && deniedError.code !== 'PGRST116') {
          // Fall back to simple query
          const { data: simpleDenied } = await supabase
            .from('device_approval_requests')
            .select('*')
            .eq('device_id', deviceId)
            .eq('organization_id', profile.organization_id)
            .eq('status', 'denied')
            .gt('reviewed_at', fiveMinutesAgo)
            .order('reviewed_at', { ascending: false })
            .limit(1);
          
          deniedRequest = simpleDenied?.[0] as DeviceApprovalRequest | null;
        }
      } catch (err) {
        console.warn('[DeviceRegistration] Error checking denied requests:', err);
      }

      const isRegistered = !!device;
      const isActive = device?.is_active ?? false;

      // Update last seen if device is registered (fire and forget)
      if (isRegistered && isActive) {
        (async () => {
          try {
            await supabase.rpc('update_device_last_seen', { p_device_id: deviceId });
          } catch {
            // Silently fail - this is non-critical
          }
        })();
      }

      return {
        isRegistered,
        isActive,
        device,
        requiresApproval: isCashier && !isRegistered,
        hasPendingRequest: !!pendingRequest,
        pendingRequest,
        wasDenied: !!deniedRequest,
        deniedRequest,
      };
    },
    enabled: !!deviceId && !!profile?.organization_id && !deviceLoading,
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: isCashier ? 10000 : false, // Poll every 10 seconds for cashiers awaiting approval
  });

  // Real-time subscription for device approval status changes
  useEffect(() => {
    if (!deviceId || !profile?.organization_id || !isCashier) return;

    // Subscribe to changes on device_approval_requests for this device
    const channel = supabase
      .channel(`device-approval-${deviceId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'device_approval_requests',
          filter: `device_id=eq.${deviceId}`,
        },
        (payload) => {
          const updatedRequest = payload.new as DeviceApprovalRequest;
          // If the request was approved or denied, refetch registration status
          if (updatedRequest.status === 'approved' || updatedRequest.status === 'denied') {
            queryClient.invalidateQueries({ queryKey: ['device-registration'] });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'registered_devices',
          filter: `device_id=eq.${deviceId}`,
        },
        () => {
          // Device was registered, refetch status
          queryClient.invalidateQueries({ queryKey: ['device-registration'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [deviceId, profile?.organization_id, isCashier, queryClient]);

  // Request device approval (for cashiers)
  const requestApproval = useMutation({
    mutationFn: async ({
      locationId,
      deviceName,
    }: {
      locationId: string;
      deviceName?: string;
    }) => {
      if (!deviceId || !profile?.organization_id || !profile?.id) {
        throw new Error('Missing required information');
      }

      const finalDeviceName = deviceName || `${deviceType === 'electron' ? 'POS Terminal' : 'Web Device'}`;

      const { data, error } = await supabase
        .from('device_approval_requests')
        .insert({
          device_id: deviceId,
          device_fingerprint: fingerprint,
          organization_id: profile.organization_id,
          requested_by: profile.id,
          requested_location_id: locationId,
          requested_device_name: finalDeviceName,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      // Create a notification for admins/managers at this location
      const requesterName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'A user';

      await supabase.from('notifications').insert({
        type: 'device_approval_request',
        title: 'Device Approval Request',
        message: `${requesterName} is requesting to register ${finalDeviceName}.`,
        location_id: locationId,
        data: {
          request_id: data.id,
          device_id: deviceId,
          device_name: finalDeviceName,
          requester_id: profile.id,
          requester_name: requesterName,
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Register a device (admin only)
  const registerDevice = useMutation({
    mutationFn: async ({
      targetDeviceId,
      deviceName,
      targetDeviceType,
      locationId,
      metadata,
    }: {
      targetDeviceId: string;
      deviceName: string;
      targetDeviceType: 'electron' | 'web' | 'mobile';
      locationId: string;
      metadata?: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc('register_device', {
        p_device_id: targetDeviceId,
        p_device_name: deviceName,
        p_device_type: targetDeviceType,
        p_location_id: locationId,
        p_metadata: metadata || null,
      });

      if (error) throw error;
      return data as RegisteredDevice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      queryClient.invalidateQueries({ queryKey: ['registered-devices'] });
    },
  });

  // Approve a device request (admin only)
  const approveRequest = useMutation({
    mutationFn: async ({
      requestId,
      deviceName,
    }: {
      requestId: string;
      deviceName?: string;
    }) => {
      console.log('[DeviceRegistration] Approving request:', requestId, 'with name:', deviceName);

      const { data, error } = await supabase.rpc('approve_device_request', {
        p_request_id: requestId,
        p_device_name: deviceName || null,
      });

      if (error) {
        console.error('[DeviceRegistration] Approval failed:', error);
        throw new Error(error.message || 'Failed to approve device request');
      }

      console.log('[DeviceRegistration] Approval successful:', data);
      return data as RegisteredDevice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      queryClient.invalidateQueries({ queryKey: ['device-approval-requests'] });
      queryClient.invalidateQueries({ queryKey: ['registered-devices'] });
    },
  });

  // Deny a device request (admin only)
  const denyRequest = useMutation({
    mutationFn: async ({
      requestId,
      notes,
    }: {
      requestId: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('deny_device_request', {
        p_request_id: requestId,
        p_notes: notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      queryClient.invalidateQueries({ queryKey: ['device-approval-requests'] });
    },
  });

  // Deactivate a device (admin only)
  const deactivateDevice = useMutation({
    mutationFn: async (deviceRecordId: string) => {
      const { data, error } = await supabase.rpc('deactivate_device', {
        p_device_record_id: deviceRecordId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      queryClient.invalidateQueries({ queryKey: ['registered-devices'] });
    },
  });

  // Reactivate a device (admin only)
  const reactivateDevice = useMutation({
    mutationFn: async (deviceRecordId: string) => {
      const { data, error } = await supabase
        .from('registered_devices')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('id', deviceRecordId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      queryClient.invalidateQueries({ queryKey: ['registered-devices'] });
    },
  });

  return {
    // Device info
    deviceId,
    deviceType,
    fingerprint,
    isElectron,

    // Registration status
    registrationStatus,
    isLoading: deviceLoading || statusLoading,
    error: statusError,
    refetch: refetchStatus,

    // Computed states
    isRegistered: registrationStatus?.isRegistered ?? false,
    isActive: registrationStatus?.isActive ?? false,
    device: registrationStatus?.device ?? null,
    deviceLocation: registrationStatus?.device?.location ?? null,
    requiresApproval: registrationStatus?.requiresApproval ?? false,
    hasPendingRequest: registrationStatus?.hasPendingRequest ?? false,
    pendingRequest: registrationStatus?.pendingRequest ?? null,
    wasDenied: registrationStatus?.wasDenied ?? false,
    deniedRequest: registrationStatus?.deniedRequest ?? null,

    // Role checks
    isCashier,
    isAdmin,

    // Mutations
    requestApproval,
    registerDevice,
    approveRequest,
    denyRequest,
    deactivateDevice,
    reactivateDevice,
  };
}

/**
 * Hook to fetch all registered devices for admin management
 */
export function useRegisteredDevices(locationId?: string) {
  const { profile, user } = useAuth();

  return useQuery({
    queryKey: ['registered-devices', profile?.organization_id, locationId],
    queryFn: async () => {
      if (!profile?.organization_id || !user) return [];

      let query = supabase
        .from('registered_devices')
        .select(`
          *,
          location:locations(id, name),
          last_user:profiles!last_user_id(first_name, last_name),
          registered_by_user:profiles!registered_by(first_name, last_name)
        `)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as (RegisteredDevice & {
        last_user?: { first_name: string | null; last_name: string | null };
        registered_by_user?: { first_name: string | null; last_name: string | null };
      })[];
    },
    enabled: !!profile?.organization_id && !!user,
  });
}

/**
 * Hook to fetch pending device approval requests for admin management
 */
export function useDeviceApprovalRequests(locationId?: string) {
  const { profile, user } = useAuth();

  // Only admins/managers can view approval requests
  const canViewRequests = profile?.role && ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(profile.role);

  return useQuery({
    queryKey: ['device-approval-requests', profile?.organization_id, locationId],
    queryFn: async () => {
      if (!profile?.organization_id || !user) return [];

      let query = supabase
        .from('device_approval_requests')
        .select(`
          *,
          requester:profiles!requested_by(first_name, last_name),
          location:locations!requested_location_id(id, name)
        `)
        .eq('organization_id', profile.organization_id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (locationId) {
        query = query.eq('requested_location_id', locationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as (DeviceApprovalRequest & {
        requester?: { first_name: string | null; last_name: string | null };
      })[];
    },
    enabled: !!profile?.organization_id && !!user && !!canViewRequests,
    refetchInterval: 30000, // Refresh every 30 seconds to catch new requests
  });
}
